import { registerTrustedImageUrlsFromMarkdown } from '@onedesk/modules/core/editor/trustedImageUrls'

/**
 * Copies the given text to the clipboard by temporarily creating a textarea and
 * using the documents `copy` command.
 * @param {string} text
 */
export const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).catch(() => {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.focus()
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
  })
}

// Keep exported for backward compatibility but no longer used for storage.
export const LOCAL_STORAGE_CLIPBOARD_KEY = 'onedesk.clipboardData'

/**
 * In-memory clipboard store. Replaces localStorage to remove the 5 MB quota
 * limit that would cause large copies (thousands of rows) to silently fail.
 * Data is scoped to the current tab session — lost on page refresh, which is
 * acceptable for copy-paste within the same application.
 */
let _inMemoryClipboard = null
let _copyGeneration = 0
let _copyAbort = null

export function startCopySession() {
  _copyGeneration += 1
  // Superseded sessions never publish data, so stop their paginated requests
  // instead of consuming bandwidth and memory alongside the latest selection.
  _copyAbort?.abort()
  _copyAbort = new AbortController()
  return { generation: _copyGeneration, signal: _copyAbort.signal }
}

export function isCopyCurrent(generation) {
  return generation === _copyGeneration
}

/**
 * Stores rich clipboard data in memory.
 * @param {string} text  TSV text representation (same string written to OS clipboard).
 * @param {Array}  json  Rich 2-D array with full cell metadata.
 */
export const storeRichClipboardInMemory = (
  text,
  json,
  rows = null,
  generation = null
) => {
  // A slower first copy must not replace a newer one the user already made.
  if (generation != null && generation !== _copyGeneration) {
    return
  }
  // internal marks a copy made in this grid. Paste keeps column alignment,
  // including read-only columns, instead of packing values into the next
  // writable field and clearing the ones in between.
  _inMemoryClipboard = { text, json, rows, internal: true }
}

// Whether the last read clipboard came from this grid. A flag instead of an
// array identity check, because the paste path can slice the rows (expand
// dialog) before they reach the store.
let _lastReadWasInternal = false

export function lastPasteWasInternal() {
  return _lastReadWasInternal
}

/**
 * Returns Markdown for a single rich-text cell copied from this grid, or null
 * for any other clipboard shape.
 *
 * @param {string} textRawData The current plain-text clipboard value.
 * @returns {string|null} The original Markdown or null.
 */
export const getRichTextClipboardContent = (textRawData) => {
  const storedText = (_inMemoryClipboard?.text || '').replaceAll('\r\n', '\n')
  const pastedText = (textRawData || '').replaceAll('\r\n', '\n')
  if (!storedText || storedText !== pastedText) {
    return null
  }

  const richClipboardData = _inMemoryClipboard?.json
  if (richClipboardData?.length !== 1 || richClipboardData[0]?.length !== 1) {
    return null
  }

  const richValue = richClipboardData[0][0]
  if (!richValue?.richText || typeof richValue.value !== 'string') {
    return null
  }
  registerTrustedImageUrlsFromMarkdown(richValue.value)
  return richValue.value
}

/**
 * This method gets the text and json data from the clipboard and from the
 * in-memory store. This is needed because we need the original metadata to be
 * able to restore the original format, but the clipboard only allows plain
 * text / html mime types.
 * @param {*} event
 * @returns {object} An object with the textRawData and jsonRawData.
 */
export const getRichClipboard = async (event) => {
  let textRawData = ''
  let jsonRawData
  _lastReadWasInternal = false

  // Capture event data synchronously: browsers may clear DataTransfer after
  // the event handler yields. The event is authoritative, even when empty.
  const clipboardData = event?.clipboardData
  let htmlRawData = ''
  if (clipboardData?.getData) {
    textRawData = clipboardData.getData('text/plain') || ''
    htmlRawData = clipboardData.getData('text/html') || ''
  } else if (typeof navigator.clipboard?.readText === 'function') {
    try {
      textRawData = (await navigator.clipboard.readText()) || ''
    } catch (e) {
      textRawData = ''
    }
  }

  if (_inMemoryClipboard?.text || _inMemoryClipboard?.rows?.length) {
    try {
      const storedText = (_inMemoryClipboard.text || '').replaceAll(
        '\r\n',
        '\n'
      )
      const pastedText = (textRawData || '').replaceAll('\r\n', '\n')
      const sameText = storedText.length > 0 && storedText === pastedText
      // Prefix and empty matches cannot establish provenance: the user may
      // have copied a smaller selection or cleared the clipboard elsewhere.
      if (sameText) {
        textRawData = _inMemoryClipboard.text
        jsonRawData = _inMemoryClipboard.json
        _lastReadWasInternal = true
        if (Array.isArray(_inMemoryClipboard.rows)) {
          return {
            textRawData,
            jsonRawData,
            htmlRawData,
            rows: _inMemoryClipboard.rows,
          }
        }
      } else {
        _inMemoryClipboard = null
      }
    } catch (e) {
      _inMemoryClipboard = null
    }
  }

  return {
    textRawData: textRawData || '',
    jsonRawData,
    htmlRawData,
    rows: null,
  }
}

/**
 * DEPRECATED: Kept for backward compatibility where the clipboard API is not available.
 * Values should be an object with mime types as key and clipboard content for this type
 * as value. This allow add the same data with multiple representation to the clipboard.
 * We can have a row saved as tsv string or as json string. Values must be strings.
 * @param {object} values object of mime types -> clipboard content.
 */
export const setRichClipboard = (values) => {
  const listener = (e) => {
    e.preventDefault()
    e.stopPropagation()
    Object.entries(values).forEach(([type, content]) => {
      e.clipboardData.setData(type, content)
    })
  }
  document.addEventListener('copy', listener)
  try {
    document.execCommand('copy')
  } finally {
    document.removeEventListener('copy', listener)
  }
}
